import { useEffect, useRef, useState, useCallback } from "react";
import { saveDraft, loadDraft, clearDraft, saveLastRigId, loadLastRigId, DRAFT_RIG_ID } from "../persistence/localStorage";
import {
  boardStateToSyncPayload,
  syncResponseToBoardState,
} from "../persistence/serializer";

const DEBOUNCE_MS = 2500;

export default function usePersistence({
  nodes,
  edges,
  rigCables,
  loadState,
  authenticated,
  apiClient,
  maxSavedRigs = 2,
  readOnly = false,
}) {
  const initialized = useRef(false);
  const hasContent = useRef(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [currentRigId, setCurrentRigId] = useState(null);
  const [rigs, setRigs] = useState([]);
  const debounceTimer = useRef(null);
  const isSyncing = useRef(false);
  const pendingSync = useRef(false);
  const stateRef = useRef({ nodes, edges, rigCables });
  const rigNameRef = useRef("My Rig");
  const draftMetaRef = useRef({});

  useEffect(() => {
    stateRef.current = { nodes, edges, rigCables };
  });

  // ---- Anonymous persistence ----
  useEffect(() => {
    if (readOnly || authenticated) return;

    const draft = loadDraft();
    if (draft) {
      loadState(draft);
      draftMetaRef.current = {
        rigName: draft.rigName,
        rigDescription: draft.rigDescription,
        rigNotes: draft.rigNotes,
      };
      hasContent.current = true;
    }
    initialized.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (readOnly || authenticated) return;
    if (!initialized.current) return;

    const hasNodes = nodes.length > 0;
    const hasEdges = edges.length > 0;

    if (hasNodes || hasEdges || hasContent.current) {
      saveDraft({
        nodes,
        edges,
        rigCables,
        rigName: draftMetaRef.current.rigName,
        rigDescription: draftMetaRef.current.rigDescription,
        rigNotes: draftMetaRef.current.rigNotes,
      });
      hasContent.current = true;
    }
  }, [nodes, edges, rigCables, authenticated, readOnly]);

  // ---- Authenticated persistence ----
  /* eslint-disable react-hooks/preserve-manual-memoization -- stable identity needed for debounce effect deps */
  const doSync = useCallback(
    async (rigId) => {
      if (!apiClient || !rigId) return;
      if (isSyncing.current) {
        pendingSync.current = true;
        return;
      }

      isSyncing.current = true;
      setSaveStatus("saving");

      try {
        const payload = boardStateToSyncPayload(
          stateRef.current.nodes,
          stateRef.current.edges,
          rigNameRef.current,
          stateRef.current.rigCables
        );
        await apiClient.syncRig(rigId, payload);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      } finally {
        isSyncing.current = false;
        if (pendingSync.current) {
          pendingSync.current = false;
          doSync(rigId);
        }
      }
    },
    [apiClient]
  );
  /* eslint-enable react-hooks/preserve-manual-memoization */

  // Load rigs on mount (authenticated)
  useEffect(() => {
    if (readOnly || !authenticated || !apiClient) return;

    let cancelled = false;

    async function init() {
      try {
        let draft = loadDraft();
        const rigList = await apiClient.getRigs();

        if (cancelled) return;
        setRigs(rigList);

        // Skip blank drafts when user already has rigs (e.g. default "My Rig" from before sign-in)
        const draftIsBlank = draft && draft.nodes.length === 0 && draft.edges.length === 0;
        if (draftIsBlank && rigList.length > 0) {
          clearDraft();
          draft = null;
        }

        // Draft flow: authenticated + localStorage draft (edit-as-copy or sign-up)
        if (draft && rigList.length < maxSavedRigs) {
          const copyName = draft.rigName
            ? `${draft.rigName} Copy`
            : "My Rig";
          const newRig = await apiClient.createRig(copyName);
          if (cancelled) return;

          // Copy description and notes from the source rig
          const draftAttrs = {};
          if (draft.rigDescription) draftAttrs.description = draft.rigDescription;
          if (draft.rigNotes) draftAttrs.notes = draft.rigNotes;
          if (Object.keys(draftAttrs).length > 0) {
            await apiClient.updateRig(newRig.id, draftAttrs);
          }

          const updatedRig = { ...newRig, ...draftAttrs };
          setCurrentRigId(updatedRig.id);
          setRigs([...rigList, updatedRig]);
          rigNameRef.current = updatedRig.name;

          // Load draft state into editor
          loadState({ nodes: draft.nodes, edges: draft.edges, rigCables: draft.rigCables });

          // Sync draft to server
          const payload = boardStateToSyncPayload(
            draft.nodes,
            draft.edges,
            updatedRig.name,
            draft.rigCables
          );
          await apiClient.syncRig(updatedRig.id, payload);

          clearDraft();
          initialized.current = true;
          return;
        }

        // Draft exists but at limit — show as draft tab
        if (draft) {
          const draftRig = { id: DRAFT_RIG_ID, name: draft.rigName || "Draft", draft: true };
          setRigs([draftRig, ...rigList]);
          setCurrentRigId(DRAFT_RIG_ID);
          rigNameRef.current = draftRig.name;
          loadState({ nodes: draft.nodes, edges: draft.edges, rigCables: draft.rigCables || [] });
          initialized.current = true;
          return;
        }

        // Normal load: restore last rig, fall back to most recent, or create one
        if (rigList.length > 0) {
          const lastId = loadLastRigId();
          const targetId = lastId && rigList.some((r) => r.id === lastId) ? lastId : rigList[0].id;
          const detail = await apiClient.getRig(targetId);
          if (cancelled) return;

          setCurrentRigId(detail.id);
          rigNameRef.current = detail.name;

          const state = syncResponseToBoardState(detail);
          loadState(state);
        } else {
          const newRig = await apiClient.createRig("My Rig");
          if (cancelled) return;

          setCurrentRigId(newRig.id);
          setRigs([newRig]);
          rigNameRef.current = newRig.name;
        }

        initialized.current = true;
      } catch {
        setSaveStatus("error");
        initialized.current = true;
      }
    }

    init();
    return () => { cancelled = true; };
  }, [authenticated, apiClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remember last active rig for reload (skip draft)
  useEffect(() => {
    if (authenticated && currentRigId && currentRigId !== DRAFT_RIG_ID) {
      saveLastRigId(currentRigId);
    }
  }, [authenticated, currentRigId]);

  // Draft tab persistence (authenticated user on draft rig)
  useEffect(() => {
    if (!authenticated || !initialized.current || currentRigId !== DRAFT_RIG_ID) return;

    saveDraft({
      nodes,
      edges,
      rigCables,
      rigName: rigNameRef.current,
    });
  }, [nodes, edges, rigCables, authenticated, currentRigId]);

  // Debounced auto-save (authenticated)
  useEffect(() => {
    if (!authenticated || !initialized.current || !currentRigId) return;
    if (currentRigId === DRAFT_RIG_ID) return;

    setSaveStatus("unsaved"); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: show unsaved immediately before debounce

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      doSync(currentRigId);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [nodes, edges, rigCables, authenticated, currentRigId, doSync]);

  const saveNow = useCallback(() => {
    if (!currentRigId || currentRigId === DRAFT_RIG_ID) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    doSync(currentRigId);
  }, [currentRigId, doSync]);

  return {
    saveStatus: authenticated ? (currentRigId === DRAFT_RIG_ID ? "local" : saveStatus) : "local",
    currentRigId,
    rigs,
    setCurrentRigId,
    setRigs,
    rigNameRef,
    stateRef,
    saveNow,
  };
}
